import { useState, useEffect, useCallback } from "react";
import { ShieldWarning, Prohibit, X } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import api, { errDetail } from "@/lib/api";

const SEV = { critical: "bg-red-50 border-red-200 text-red-800", warning: "bg-amber-50 border-amber-200 text-amber-800", info: "bg-gray-50 border-gray-200 text-gray-700" };
const DOT = { critical: "🔴", warning: "🟠", info: "🟢" };

export default function AdminSecurity() {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState("open");
  const [cfg, setCfg] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/security-events", { params: { status } }).then((r) => setEvents(r.data)).catch(() => {});
    api.get("/admin/security-settings").then((r) => setCfg(r.data)).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const dismiss = async (e) => {
    try { await api.put(`/admin/security-events/${e.id}/dismiss`); toast.success("Dismissed"); load(); } catch (err) { toast.error(errDetail(err)); }
  };
  const disable = async (e) => {
    if (!window.confirm(`Disable account ${e.email}?`)) return;
    try { await api.put(`/admin/users/${e.user_id}/disable`); toast.success("Account disabled"); load(); } catch (err) { toast.error(errDetail(err)); }
  };
  const saveCfg = async () => {
    try { await api.put("/admin/security-settings", Object.fromEntries(Object.entries(cfg).map(([k, v]) => [k, Number(v)]))); toast.success("Thresholds saved"); } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <AdminShell title="Security Center" subtitle="Anomaly warnings — you decide, nothing is auto-disabled." testId="admin-security-page">
      <div className="flex items-center gap-2 mb-4">
        <select data-testid="security-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm">
          <option value="open">Open</option><option value="dismissed">Dismissed</option><option value="">All</option>
        </select>
        <span data-testid="security-open-count" className="text-sm text-gray-500">{events.length} event{events.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="space-y-2 mb-8" data-testid="security-events-list">
        {events.map((e) => (
          <div key={e.id} data-testid={`security-event-${e.id}`} className={`border rounded-md p-4 ${SEV[e.severity] || SEV.info}`}>
            <div className="flex items-center gap-2 text-sm font-semibold">
              {DOT[e.severity]} <span className="uppercase text-xs">{e.severity}</span>
              <span>{e.email}</span>
              <span className="ml-auto text-xs font-normal opacity-70">{dayjs(e.created_at).format("MMM D, HH:mm")}</span>
            </div>
            <p className="text-sm mt-1">{e.evidence} <span className="opacity-60">({e.rule})</span></p>
            {e.status === "open" && (
              <div className="flex gap-2 mt-2">
                <button data-testid={`security-disable-${e.id}`} onClick={() => disable(e)}
                  className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded px-3 py-1.5"><Prohibit size={12} /> Disable account</button>
                <button data-testid={`security-dismiss-${e.id}`} onClick={() => dismiss(e)}
                  className="flex items-center gap-1 border border-current/30 text-xs font-semibold rounded px-3 py-1.5 hover:bg-white/50"><X size={12} /> Dismiss</button>
              </div>
            )}
          </div>
        ))}
        {events.length === 0 && (
          <div data-testid="security-empty" className="border border-dashed border-gray-300 rounded-md p-10 text-center text-sm text-gray-400">
            <ShieldWarning size={26} className="mx-auto mb-2 opacity-50" /> No {status || "recorded"} security events — all quiet.
          </div>
        )}
      </div>
      {cfg && (
        <div className="border border-gray-200 rounded-md p-5" data-testid="security-thresholds">
          <h2 className="font-heading font-bold text-gray-900 mb-3">Detection thresholds</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {[["burn_multiplier", "Token burn × avg"], ["otp_fail_threshold", "OTP fails /hr"], ["rate_hits_per_hour", "API hits /hr"],
              ["sends_per_hour", "Sends /hr"], ["ips_per_30min", "IPs /30min"], ["api_key_rate_per_min", "API key req/min limit"],
              ["billing_rate_per_hour", "Billing attempts/hr limit"]].map(([k, label]) => (
              <div key={k}>
                <label className="block text-[10px] uppercase text-gray-400 mb-0.5">{label}</label>
                <input data-testid={`threshold-${k}`} type="number" value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
              </div>
            ))}
          </div>
          <button data-testid="save-thresholds-btn" onClick={saveCfg}
            className="mt-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-semibold">Save thresholds</button>
        </div>
      )}
    </AdminShell>
  );
}
